/**
 * CR305B: RewardBoxBlock (V2)
 * CR312: Extended to support POINTS deals (Points Earned)
 * CR313: Extended to support DISCOUNT deals (Discount / Savings)
 * CR314: Extended to support IN_KIND deals (Free Item / Perk)
 * CR415: BONUS reward label uses purchase retailer by default, never issuer
 * CR416: BONUS reward label uses bonus-side brand only; feature-flagged promo merchant fallback
 * 
 * Displays:
 * - BONUS: "Bonus gift card (Retailer)" — retailer-default, never issuer
 * - POINTS: Points earned
 * - DISCOUNT: Discount percentage or amount saved
 * - IN_KIND: Free item/perk description
 * 
 * Hide-when-empty: Does not render if reward data is missing.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { FC } from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { resolveBonusValueModel, formatCurrency } from "@/lib/deal-cards/bonusValueModel";
import { getRewardLabel } from "@/lib/deals/dealCardDisplay";
import { Gift, Star, Percent, Ticket } from "lucide-react";

/** Sub-block for POINTS deals */
const PointsRewardBox: FC<{ deal: Deal; block: DealCardBlockProps<Deal>["block"]; context: DealCardBlockProps<Deal>["context"] }> = ({ deal, block, context }) => {
  // CR416: Prefer server-computed reward fields
  const serverLabel = (deal as any).reward_label as string | null | undefined;
  const serverValue = (deal as any).reward_value_primary as string | null | undefined;

  const pointsAmount = deal.points_amount;
  const pointsProgram = deal.points_program_name;
  const spendAmount = deal.spend_amount ?? deal.qualifying_spend_cad;
  
  const clientValue = (pointsAmount && pointsAmount > 0) ? `${pointsAmount.toLocaleString()} pts` : null;
  const displayValue = serverValue || clientValue;
  if (!displayValue) return null;
  
  const labelText = serverLabel || context.labels?.reward_box_title || context.labels?.reward_title || "Points Earned";
  
  return (
    <div 
      className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-md border border-purple-200"
      data-block="reward_box"
      data-block-id={block.id}
    >
      <div className="flex items-center gap-2">
        <Star className="h-4 w-4 text-purple-600" />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-purple-900">{labelText}</span>
          {pointsProgram && <span className="text-xs text-purple-600">{pointsProgram}</span>}
        </div>
      </div>
      <div className="text-right">
        <span className="text-lg font-semibold text-purple-700">{displayValue} pts</span>
        {spendAmount && <span className="block text-xs text-purple-600">per ${spendAmount}</span>}
      </div>
    </div>
  );
};

/** Sub-block for DISCOUNT deals — CR416: defensive fallback from eligibility_requirements, CR417: cross-category */
const DiscountRewardBox: FC<{ deal: Deal; block: DealCardBlockProps<Deal>["block"]; context: DealCardBlockProps<Deal>["context"] }> = ({ deal, block, context }) => {
  // CR416: Prefer server-computed reward fields
  const serverLabel = (deal as any).reward_label as string | null | undefined;
  const serverValue = (deal as any).reward_value_primary as string | null | undefined;

  const discountPercent = deal.discount_percent;
  const discountAmount = (deal as any).discount_amount_cad ?? deal.computed_value_cad;
  const payAmount = (deal as any).pay_amount_cad ?? deal.qualifying_spend_cad;
  const spendAmount = deal.spend_amount ?? deal.qualifying_spend_cad;
  
  // CR416: Defensive fallback - derive from eligibility_requirements
  const canonical = (deal as any).canonical_deal_json;
  const erGetAmount = canonical?.eligibility_requirements?.get?.amount_cad;
  const effectiveDiscountAmount = discountAmount ?? erGetAmount;
  
  // CR417: Extract cross-category discount target from canonical JSON, headline, or fine_print
  const finePrint = canonical?.fine_print ?? (deal as any).fine_print;
  const discountTarget = canonical?.reward?.discount_target 
    ?? canonical?.discount_target 
    ?? detectDiscountTargetFromHeadline(deal.headline_mechanic)
    ?? detectDiscountTargetFromFinePrint(finePrint);
  
  let derivedPercent: number | null = null;
  if (!discountPercent && spendAmount && payAmount && spendAmount > payAmount) {
    derivedPercent = Math.round(((spendAmount - payAmount) / spendAmount) * 100);
  }
  
  const effectivePercent = discountPercent ?? derivedPercent;
  
  // Client-computed display value — CR416: use effectiveDiscountAmount
  const clientValue = (effectiveDiscountAmount && effectiveDiscountAmount > 0)
    ? `$${Number(effectiveDiscountAmount).toFixed(0)} OFF`
    : effectivePercent 
      ? `${effectivePercent}% OFF`
      : null;

  const displayValue = serverValue || clientValue;
  if (!displayValue) return null;
  
  // CR416: Default label for DISCOUNT is "Discount", not "Reward"
  const baseLabel = serverLabel || context.labels?.reward_box_title || context.labels?.reward_title || "Discount";
  const labelText = discountTarget 
    ? `${baseLabel} — ${capitalizeFirst(discountTarget)}` 
    : baseLabel;
  
  return (
    <div 
      className="flex items-center justify-between py-2 px-3 bg-orange-50 rounded-md border border-orange-200"
      data-block="reward_box"
      data-block-id={block.id}
    >
      <div className="flex items-center gap-2">
        <Percent className="h-4 w-4 text-orange-600" />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-orange-900">{labelText}</span>
          {discountTarget && (
            <span className="text-xs text-orange-600">on {discountTarget}</span>
          )}
        </div>
      </div>
      <span className="text-lg font-semibold text-orange-700">{displayValue}</span>
    </div>
  );
};

/** CR417: Detect discount target from headline text */
function detectDiscountTargetFromHeadline(headline?: string | null): string | null {
  if (!headline) return null;
  const match = headline.match(/\$\d+(?:\.\d+)?\s+(?:off|OFF)\s+(\w[\w\s]*?)(?:\s+when|\s+with|\s+if|\.|,|$)/i)
    ?? headline.match(/save\s+\$\d+(?:\.\d+)?\s+on\s+(\w[\w\s]*?)(?:\s+when|\s+with|\s+if|\.|,|$)/i);
  if (match) {
    const target = match[1].trim();
    // Exclude gift card references
    if (/gift\s*card|bonus\s*card/i.test(target)) return null;
    if (target.length > 2 && target.length < 40) return target;
  }
  return null;
}

/** CR417: Detect discount target from fine print (e.g., "Minimum $30 flower spend") */
function detectDiscountTargetFromFinePrint(finePrint?: string | null): string | null {
  if (!finePrint) return null;
  const match = finePrint.match(/minimum\s+\$?\d+(?:\.\d+)?\s+(\w[\w\s]*?)\s+spend/i)
    ?? finePrint.match(/min(?:imum)?\s+\d+(?:\.\d+)?\s+(\w[\w\s]*?)\s+spend/i);
  if (match) {
    const target = match[1].trim();
    if (/gift\s*card|bonus\s*card/i.test(target)) return null;
    if (target.length > 2 && target.length < 40) return target;
  }
  return null;
}

/** Capitalize first letter */
function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Sub-block for IN_KIND deals */
const InKindRewardBox: FC<{ deal: Deal; block: DealCardBlockProps<Deal>["block"]; context: DealCardBlockProps<Deal>["context"] }> = ({ deal, block, context }) => {
  const inKindRewardName = (deal as any).in_kind_reward_name 
    ?? (deal as any).reward_description 
    ?? (deal as any).free_item_name
    ?? deal.headline_mechanic;
  const inKindRewardQty = (deal as any).in_kind_reward_qty;
  const inKindConstraints = (deal as any).in_kind_constraints ?? (deal as any).reward_constraints;
  
  if (!inKindRewardName) return null;
  
  const labelText = context.labels?.reward_box_title ?? context.labels?.reward_title ?? "Free Item / Perk";
  let displayValue = inKindRewardName;
  if (inKindRewardQty && inKindRewardQty > 1) {
    displayValue = `${inKindRewardQty}x ${inKindRewardName}`;
  }
  
  return (
    <div 
      className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-md border border-amber-200"
      data-block="reward_box"
      data-block-id={block.id}
    >
      <div className="flex items-center gap-2">
        <Ticket className="h-4 w-4 text-amber-600" />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-amber-900">{labelText}</span>
          {inKindConstraints && <span className="text-xs text-amber-600">{inKindConstraints}</span>}
        </div>
      </div>
      <span className="text-base font-semibold text-amber-700 text-right max-w-[150px] truncate">
        {displayValue}
      </span>
    </div>
  );
};

/** Sub-block for BONUS deals — CR415: uses retailer, never issuer; CR416: prefers server-computed fields */
const BonusRewardBox: FC<{ deal: Deal; block: DealCardBlockProps<Deal>["block"]; context: DealCardBlockProps<Deal>["context"] }> = ({ deal, block, context }) => {
  // CR416: Prefer server-computed reward fields when available
  const serverLabel = (deal as any).reward_label as string | null | undefined;
  const serverValue = (deal as any).reward_value_primary as string | null | undefined;

  // Fallback: client-side derivation
  const valueModel = resolveBonusValueModel(deal);
  const { rewardAmount, sectionHasData } = valueModel;
  
  const displayValue = serverValue || (sectionHasData.reward ? formatCurrency(rewardAmount) : null);
  
  // CR416: If no value at all, hide — never render empty reward box
  if (!displayValue) return null;
  
  // CR416: Server label wins, then client-computed, then context override
  const computedLabel = getRewardLabel(deal);
  const labelText = serverLabel || computedLabel || context.labels?.reward_box_title || context.labels?.reward_title || "Bonus Value";
  
  return (
    <div 
      className="flex items-center justify-between py-2 px-3 bg-accent/30 rounded-md border border-accent/50"
      data-block="reward_box"
      data-block-id={block.id}
    >
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium text-foreground">{labelText}</span>
      </div>
      <span className="text-lg font-semibold text-primary">{displayValue}</span>
    </div>
  );
};

export const RewardBoxBlock: FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  if (!block.enabled) return null;
  
  const { category: dealCategory } = getDealType(deal);
  
  switch (dealCategory) {
    case "POINTS":
      return <PointsRewardBox deal={deal} block={block} context={context} />;
    case "DISCOUNT":
      return <DiscountRewardBox deal={deal} block={block} context={context} />;
    case "IN_KIND":
      return <InKindRewardBox deal={deal} block={block} context={context} />;
    case "BONUS":
      return <BonusRewardBox deal={deal} block={block} context={context} />;
    default:
      return null;
  }
};

export default RewardBoxBlock;
